import React from 'react';

const RevolutionizingSurveyResearch = ({ navigateTo }) => {
  return (
    <div className="bg-black pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-6">

        {/* Back button */}
        <button 
          onClick={() => navigateTo('blog-title')} 
          className="mb-8 flex items-center text-gray-400 hover:text-white transition"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to Blog
        </button>

        {/* Blog post header */}
        <header className="mb-10">
          <h1 className="text-4xl font-extrabold text-white mb-4">Why HumanAI is Rebuilding the Future of Survey Research</h1>
          <div className="flex items-center text-sm text-gray-400 mb-6">
            <span>Apr 2025</span>
            <span className="mx-2">•</span>
            <span>8 min read</span>
            <span className="mx-2">•</span>
            <span>Vision</span>
          </div>
          <div className="flex items-center">
            <img 
              src={process.env.PUBLIC_URL + '/img/sharif-headshot.jpg'} 
              alt="Dr. Sharif Amlani" 
              className="h-10 w-10 rounded-full mr-3"
            />
            <div>
              <p className="text-white font-medium">Dr. Sharif Amlani</p>
              <p className="text-gray-400 text-sm">Founder & CEO, HumanAI</p>
            </div>
          </div>
        </header>

        {/* Blog content */}
        <div className="prose prose-lg prose-invert max-w-none space-y-6">

          <p className="text-lg">
            Our mission at HumanAI is simple but bold: <strong>to innovate so that humanity can better understand itself.</strong> We believe the way we measure public opinion—how we listen to people, how we understand society—is broken. And we’re building something better.
          </p>

          <h2 className="text-2xl font-bold text-white border-b border-gray-700 pb-1">The Problem We’re Solving</h2>

          <p>
            Traditional surveys are slow, expensive, and increasingly unreliable. Response rates have collapsed. Human participants are inattentive or biased. Mistakes in survey design are often irreversible. And despite the costs, the insights we get are narrow, static, and often misleading.
          </p>

          <p>
            In an era that moves in real time—where political campaigns shift by the hour, where brands live or die by a single tweet—waiting weeks for flawed survey results is no longer an option.
          </p>

          <h2 className="text-2xl font-bold text-white border-b border-gray-700 pb-1">What We Believe</h2>

          <p>
            We believe the next great leap in social science, public opinion, and user insight will not come from new questions—but from new respondents.
          </p>

          <p>
            AI agents, modeled to emulate real human cognition, emotion, and behavior, represent a generational breakthrough. They can reflect not only demographics—but psychographics, biases, beliefs, uncertainty, and change. These agents aren’t just fast—they’re introspective. They don’t just respond—they explain themselves. And they never ghost you at 60% completion.
          </p>

          <h2 className="text-2xl font-bold text-white border-b border-gray-700 pb-1">What We're Building</h2>

          <p>
            HumanAI is a platform built on a powerful foundation: the Human Engine. It is a system of synthetic respondents capable of emulating how real people think, feel, and decide.
          </p>

          <p>From that engine, we’re building a product suite that includes:</p>

          <ul className="space-y-2">
            <li><strong>🧠 Survey Completion:</strong> Upload or create surveys, and get responses from diverse synthetic populations in minutes.</li>
            <li><strong>💬 Message Testing:</strong> Evaluate the emotional and cognitive impact of messages on AI respondents before launch.</li>
            <li><strong>🔍 Causal Experiments:</strong> Simulate randomization and counterfactuals to test theories and interventions.</li>
            <li><strong>📈 Live Event Reactions:</strong> Agents that evolve in real-time based on news and social media inputs.</li>
            <li><strong>📊 Benchmarking:</strong> Compare AI results with human panels to gauge representativeness and performance.</li>
            <li><strong>🧪 Cognitive Bias Testing:</strong> Agents that replicate behavioral science experiments (e.g., framing, anchoring, illusory truth).</li>
            <li><strong>📝 Chat With Respondents:</strong> Go beyond the what and ask the why—directly from the AI respondent's perspective.</li>
          </ul>

          <h2 className="text-2xl font-bold text-white border-b border-gray-700 pb-1">Our Vision</h2>

          <p>
            This isn’t just a better survey tool. This is a new way of understanding human behavior.
          </p>

          <p>
            We imagine a world where any researcher, policymaker, strategist, or innovator can spin up a representative sample of Americans—or Canadians, or Gen Zs, or teachers, or conservatives—and ask them anything. Test anything. Learn anything. In seconds.
          </p>

          <p>
            As the foundational models improve, our synthetic populations will grow more human. As our emulation improves, our insights will grow more precise. And as more people build on this platform, we’ll collectively create the most flexible, scalable, and intelligent system for understanding people the world has ever seen.
          </p>

          <h2 className="text-2xl font-bold text-white border-b border-gray-700 pb-1">The Road Ahead</h2>

          <p>
            We’re starting with a few core modules: synthetic survey completion, message testing, and cognitive bias replication. But the long game is bigger: agent-based experimentation, generative focus groups, and real-time media labs that respond to culture as it happens.
          </p>

          <p>
            This is our moonshot. A future where everyone—from a PhD student to a presidential campaign—can explore how the world thinks without ever launching a poll.
          </p>

          <p className="text-lg font-semibold text-white">
            We’re not just building faster surveys. We’re building an engine for human insight.
          </p>

          <p>
            If you're as excited about this as we are, reach out. Or better yet, build with us.
          </p>

          <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 mt-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Ready to Try Synthetic Respondents?</h3>
                <p className="text-gray-300">Experience the future of market research with HumanAI's synthetic respondent technology.</p>
              </div>
              <a 
                href="https://www.humanaienterprise.com/auth" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-pink-600 hover:bg-pink-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 whitespace-nowrap"
              >
                Use It Free
              </a>
            </div>
          </div>
          
          <p className="mt-6 font-semibold">— Sharif From HumanAI</p>

        </div>
      </div>
    </div>
  );
};

export default RevolutionizingSurveyResearch;
